import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CSVLink } from 'react-csv';
import { useTable, useSortBy, useFilters, useGlobalFilter } from 'react-table';
import './AdminPortal.css';
import { MdOutlineArrowDropDown, MdOutlineArrowDropUp } from "react-icons/md";
import { splitCamelCase, sqFormatDate, replaceNullOrUndefined, arrivalDatesFormatted, sqlDateTime } from './functions/functions.js';
import { TbDownload } from "react-icons/tb";
import { MdEmail } from "react-icons/md";
import { HiDocumentText } from "react-icons/hi2";
import { IoHelpCircle } from "react-icons/io5";
import Navbar from './Navbar.js';
import htmlDocx from 'html-docx-js/dist/html-docx';
import { useAirportPickupData } from './AirportPickupDataContext';
import * as XLSX from 'xlsx';
const timeSlots = ['10:30am', '12:30pm', '2:30pm', '4:30pm', '6:30pm', '8:30pm', '10:00pm'];

function AdminPortal() {
    const navigate = useNavigate();
    const [airportPickupData, setAirportPickupData] = useState([]);
    const [mobileData, setMobileData] = useState([]);
    const [activeTab, setActiveTab] = useState('all'); // 'portal', 'mobile', or 'all'
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
    const [filteredData, setFilteredData] = useState([]);

    //Provider
    const { updateAirportPickupData } = useAirportPickupData();

    //Paginate data
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 50;

    // Add state for file upload
    const [uploading, setUploading] = useState(false);
    const [isTouchDevice, setIsTouchDevice] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
        const update = () => setIsTouchDevice(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        filterData();
    }, [startDate, endDate, selectedTimeSlot, airportPickupData, mobileData, activeTab]);

    const fetchData = async () => {
        try {
            // Fetch portal data
            const portalResponse = await fetch(`${process.env.REACT_APP_AIRPORT_PICKUP_SERVER}/api/getAirportPickupData?_=${new Date().getTime()}`);
            const portalJsonData = await portalResponse.json();
            //console.log('Portal data response:', portalJsonData);
            setAirportPickupData(portalJsonData || []);
            updateAirportPickupData(portalJsonData || []);

            // Fetch mobile data
            const mobileResponse = await fetch(`${process.env.REACT_APP_AIRPORT_PICKUP_SERVER}/api/getAirportPickupMobileData?_=${new Date().getTime()}`);
            const mobileJsonData = await mobileResponse.json();
            //console.log('Mobile data response:', mobileJsonData);
            setMobileData(mobileJsonData || []);
        } catch (error) {
            console.error('Error fetching formData:', error.message);
            // Set empty arrays as fallback
            setAirportPickupData([]);
            setMobileData([]);
            updateAirportPickupData([]);
        }
    };

    // Function to standardize mobile data format to match portal format
    const standardizeMobileData = (mobileData) => {
        return mobileData.map(item => {
            const standardized = { ...item };
            
            // Standardize CreateByDate format
            if (item.CreateByDate) {
                // Convert from "25/09/2024 16:58" to "2024-09-25 16:58:00" format
                const dateMatch = item.CreateByDate.match(/(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/);
                if (dateMatch) {
                    const [, day, month, year, hour, minute] = dateMatch;
                    standardized.CreateByDate = `${year}-${month}-${day} ${hour}:${minute}:00`;
                }
            }
            
            // Standardize TargetDate format
            if (item.TargetDate) {
                // Convert from "25/09/2024" to "2024-09-25" format
                const dateMatch = item.TargetDate.match(/(\d{2})\/(\d{2})\/(\d{4})/);
                if (dateMatch) {
                    const [, day, month, year] = dateMatch;
                    standardized.TargetDate = `${year}-${month}-${day}`;
                }
            }
            
            // Standardize TimeSlot format
            if (item.TimeSlot) {
                // Convert from "12:20" or "22:00" to "12:30pm" or "10:00pm" format
                const timeMatch = item.TimeSlot.match(/(\d{1,2}):(\d{2})/);
                if (timeMatch) {
                    const [, hour, minute] = timeMatch;
                    const hourNum = parseInt(hour);
                    const minuteNum = parseInt(minute);
                    
                    // Map to closest portal time slot
                    const timeSlots = [
                        { time: '10:30am', hour: 10, minute: 30 },
                        { time: '12:30pm', hour: 12, minute: 30 },
                        { time: '2:30pm', hour: 14, minute: 30 },
                        { time: '4:30pm', hour: 16, minute: 30 },
                        { time: '6:30pm', hour: 18, minute: 30 },
                        { time: '8:30pm', hour: 20, minute: 30 },
                        { time: '10:00pm', hour: 22, minute: 0 }
                    ];
                    
                    // Find closest time slot
                    let closestSlot = timeSlots[0];
                    let minDifference = Math.abs((hourNum * 60 + minuteNum) - (closestSlot.hour * 60 + closestSlot.minute));
                    
                    timeSlots.forEach(slot => {
                        const difference = Math.abs((hourNum * 60 + minuteNum) - (slot.hour * 60 + slot.minute));
                        if (difference < minDifference) {
                            minDifference = difference;
                            closestSlot = slot;
                        }
                    });
                    
                    standardized.TimeSlot = closestSlot.time;
                }
            }
            
            return standardized;
        });
    };

    const filterData = () => {
        let data = [];
        
        if (activeTab === 'portal') {
            data = airportPickupData || [];
        } else if (activeTab === 'mobile') {
            data = standardizeMobileData(mobileData || []);
        } else if (activeTab === 'all') {
            // Combine both datasets and sort by Create By Date
            const portalData = (airportPickupData || []).map(item => ({ ...item, dataSource: 'portal' }));
            const mobileDataWithSource = standardizeMobileData(mobileData || []).map(item => ({ ...item, dataSource: 'mobile' }));
            
            data = [...portalData, ...mobileDataWithSource].sort((a, b) => {
                const dateA = new Date(a.CreateByDate || 0);
                const dateB = new Date(b.CreateByDate || 0);
                return dateB - dateA; // Sort in descending order (newest first)
            });
        }

        if (startDate && endDate) {
            const adjustedEndDate = new Date(endDate.getTime() + (24 * 60 * 60 * 1000));
            data = data.filter(item => {
                let date;
                if (item.TargetDate) {
                    // Handle both standardized format (YYYY-MM-DD) and original mobile format (DD/MM/YYYY)
                    if (item.TargetDate.includes('/')) {
                        // Original mobile format: DD/MM/YYYY
                        const [day, month, year] = item.TargetDate.split('/');
                        date = new Date(year, month - 1, day);
                    } else {
                        // Standardized format: YYYY-MM-DD
                        date = new Date(item.TargetDate);
                    }
                } else {
                    date = new Date(0); // Invalid date
                }
                return date > startDate && date <= adjustedEndDate;
            });
        }

        if (selectedTimeSlot) {
            data = data.filter(item => {
                if (!item.TimeSlot) return false;
                
                // If the item has already been standardized, compare directly
                if (timeSlots.includes(item.TimeSlot)) {
                    return item.TimeSlot === selectedTimeSlot;
                }
                
                // If it's still in original mobile format, standardize it for comparison
                const timeMatch = item.TimeSlot.match(/(\d{1,2}):(\d{2})/);
                if (timeMatch) {
                    const [, hour, minute] = timeMatch;
                    const hourNum = parseInt(hour);
                    const minuteNum = parseInt(minute);
                    
                    // Map to closest portal time slot
                    const timeSlotsMap = [
                        { time: '10:30am', hour: 10, minute: 30 },
                        { time: '12:30pm', hour: 12, minute: 30 },
                        { time: '2:30pm', hour: 14, minute: 30 },
                        { time: '4:30pm', hour: 16, minute: 30 },
                        { time: '6:30pm', hour: 18, minute: 30 },
                        { time: '8:30pm', hour: 20, minute: 30 },
                        { time: '10:00pm', hour: 22, minute: 0 }
                    ];
                    
                    // Find closest time slot
                    let closestSlot = timeSlotsMap[0];
                    let minDifference = Math.abs((hourNum * 60 + minuteNum) - (closestSlot.hour * 60 + closestSlot.minute));
                    
                    timeSlotsMap.forEach(slot => {
                        const difference = Math.abs((hourNum * 60 + minuteNum) - (slot.hour * 60 + slot.minute));
                        if (difference < minDifference) {
                            minDifference = difference;
                            closestSlot = slot;
                        }
                    });
                    
                    return closestSlot.time === selectedTimeSlot;
                }
                
                return false;
            });
        }

        setFilteredData(data);
    };

    const handleApprove = async (row) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_AIRPORT_PICKUP_SERVER}/api/updateStatus`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    indexs: row.original.Indexs,
                    status: 'Approve',
                    approveByDate: sqlDateTime(new Date()),
                }),
            })
            if (response.ok) {
                console.log('Data inserted successfully');
            } else {
                console.error('Failed to insert data');
            }
        } catch (error) {
            console.error('Error:', error);
        }
        
        // Update the local state
        if (activeTab === 'all') {
            // For all data tab, update the appropriate source dataset
            if (row.original.dataSource === 'portal') {
                const updatedPortalData = [...airportPickupData];
                const portalIndex = updatedPortalData.findIndex(item => item.Indexs === row.original.Indexs);
                if (portalIndex !== -1) {
                    updatedPortalData[portalIndex].Status = 'Approve';
                    setAirportPickupData(updatedPortalData);
                }
            }
        }
        
        const updatedData = [...filteredData];
        updatedData[row.index].Status = 'Approve';
        setFilteredData(updatedData);
    };


    const handleReject = async (row) => {
        const confirmed = window.confirm(`Are you sure you want to reject?`);
        if (confirmed) {

            const inputText = window.prompt('Please enter your reject reson (limit 100 character):');

            if (inputText === null) {
                alert('Please enter reason to proceed.');
            } else if (inputText.trim().length === 0) {
                alert('Please enter reason to proceed.');
            } else if (inputText.length > 100) {
                alert('Please enter up to 100 character only.');
            } else {
                alert(`Rejected with key: "${inputText}".`);
                try {
                    const response = await fetch(`${process.env.REACT_APP_AIRPORT_PICKUP_SERVER}/api/updateStatus`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            indexs: row.original.Indexs,
                            status: 'Reject:' + inputText,
                            approveByDate: sqlDateTime(new Date()),
                        }),
                    })
                    if (response.ok) {
                        console.log('Data inserted successfully');
                    } else {
                        console.error('Failed to insert data');
                    }
                } catch (error) {
                    console.error('Error:', error);
                }
                
                // Update the local state
                if (activeTab === 'all') {
                    // For all data tab, update the appropriate source dataset
                    if (row.original.dataSource === 'portal') {
                        const updatedPortalData = [...airportPickupData];
                        const portalIndex = updatedPortalData.findIndex(item => item.Indexs === row.original.Indexs);
                        if (portalIndex !== -1) {
                            updatedPortalData[portalIndex].Status = 'Reject:' + inputText;
                            setAirportPickupData(updatedPortalData);
                        }
                    }
                }
                
                const updatedData = [...filteredData];
                updatedData[row.index].Status = 'Reject:' + inputText;
                setFilteredData(updatedData);
            }
           


        }
    };


    const handleNotify = async (row) => {
        const confirmed = window.confirm(`Are you sure you want to send an email and notify ${row.original.Email}?`);
        if (confirmed) {
            // Show loading message
            
            try {
                const response = await fetch(`${process.env.REACT_APP_AIRPORT_PICKUP_SERVER}/api/send-email`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ 
                        status: row.original.Status, 
                        patientName: row.original.PatientName, 
                        companionsName: row.original.CompanionsName, 
                        email: row.original.Email, 
                        targetDate: row.original.TargetDate, 
                        timeSlot: row.original.TimeSlot, 
                        companions: row.original.CompanionsName 
                    }),
                });

                const result = await response.json();

                // Add 2 second delay before showing success/failure message
                await new Promise(resolve => setTimeout(resolve, 2000));

                if (result.success === true) {
                    window.alert(`Email sent successfully to ${row.original.Email}.`);

                    try {
                        const response = await fetch(`${process.env.REACT_APP_AIRPORT_PICKUP_SERVER}/api/updateisNotify`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                indexs: row.original.Indexs,
                                isNotify: 1,
                            }),
                        })
                        if (response.ok) {
                            console.log('Data inserted successfully');
                            
                            // Update the local state
                            if (activeTab === 'all') {
                                // For all data tab, update the appropriate source dataset
                                if (row.original.dataSource === 'portal') {
                                    const updatedPortalData = [...airportPickupData];
                                    const portalIndex = updatedPortalData.findIndex(item => item.Indexs === row.original.Indexs);
                                    if (portalIndex !== -1) {
                                        updatedPortalData[portalIndex].isNotify = true;
                                        setAirportPickupData(updatedPortalData);
                                    }
                                }
                            }
                            
                            const updatedData = [...filteredData];
                            updatedData[row.index].isNotify = true;
                            setFilteredData(updatedData);
                        } else {
                            const errorData = await response.json();
                            console.error('Failed to insert data:', errorData);
                            window.alert(`Failed to update notification status: ${errorData.error || 'Unknown error'}`);
                        }
                    } catch (error) {
                        console.error('Error:', error);
                        window.alert(`Failed to update notification status: ${error.message}, please wait for awhile and try again.`);
                    }
                } else {
                    // Show the specific error message from the server
                    window.alert(`Failed to send email: ${result.error || 'Unknown error occurred'}`);
                }
            } catch (error) {
                // Add 2 second delay before showing error message
                await new Promise(resolve => setTimeout(resolve, 2000));
                console.error('Error sending email:', error);
                // Try to parse the error response if it exists
                let errorMessage = 'Unknown error occurred';
                try {
                    const errorData = await error.response?.json();
                    errorMessage = errorData?.error || error.message;
                } catch (e) {
                    errorMessage = error.message;
                }
                window.alert(`Failed to send email: ${errorMessage}, please wait for awhile and try again.`);
            }
        }
    };


    const updateHoliday = () => {
        navigate('/updateHoliday');
    }


    const updateSpecialist = () => {
        navigate('/updateSpecialist');
    }


    const handlePrintSlip = () => {
        const formatDate = (date) => {
            return new Date(date).toISOString().split('T')[0];
        };

        let totalPassenger = 0;

        rows.forEach(row => {

            if (row.original.Status === 'Approve'|| row.original.Status === 'Confirmed') {
                totalPassenger += 1;

                if (row.original.CompanionsName && row.original.CompanionsName.trim() !== '') {
                    totalPassenger += 1;
                }
            }
        });

        const arrivalDates = [...new Set(filteredData.map(data => formatDate(data.TargetDate)))];
        const pickupTimes = [...new Set(rows.map(row => row.original.TimeSlot))];

        const companions = [];


        const replaceNullOrUndefined = (value) => value == null ? '' : value;

        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <title>IH PATIENTS' AIRPORT PICK-UP FORM</title>
</head>
<body>
    <img src="./logo512.png" alt="Logo" style="width: 35px; height: auto; float: left; ">
    <h2 style="text-align: center;">IH PATIENTS' AIRPORT PICK-UP FORM</h2>
    <p>ARRIVAL TIME: <strong>${arrivalDates.join(', ')}</strong></p>
    <p>PICKUP TIME: <strong>${pickupTimes.join(', ')}</strong></p>
    <p>NO OF PAX: <strong>${totalPassenger}</strong></p>
    <p>NAMELIST:</p>
    <table>
        <thead>
            <tr>
                <th>NAME</th>
                <th>PASSPORT NO</th>
                <th>FLIGHT NO</th>
                <th>PT/COMPANION</th>
                <th>REMARKS</th>
            </tr>
        </thead>
        <tbody>
            ${rows
           // .filter(row => row.original.Status === 'Approve')
            .map((row) => {

                if (row.original.Status !== 'Approve' && row.original.Status !== 'Confirmed') {
                    return ''; // Ignore rows with status other than 'Approve'
                } 

            const companionsData = filteredData.filter(data =>
                data.CompanionsName &&
                data.CompanionsName.length > 1 &&
                data.CompanionsName !== '-' &&
                data.CompanionsName !== 'No' &&
             //   data.CompanionsPassportNum &&
             (data.Status === 'Approve' || data.Status === 'Confirmed') &&
                data.PatientName.includes(row.original.PatientName)
            );
            const companionsRows = companionsData.map(companion => `
                    <tr>
                        <td>${replaceNullOrUndefined(companion.CompanionsName)}</td>
                        <td>${replaceNullOrUndefined(companion.CompanionsPassportNum)}</td>
                        <td>${replaceNullOrUndefined(row.original.FlightNumber)}</td>
                        <td>Companion</td>
                        <td> </td>
                    </tr>
                `).join('');
            companions.push(...companionsData);
            return `
                    <tr>

                        <td>${replaceNullOrUndefined(row.original.PatientName)}</td>
                        <td>${replaceNullOrUndefined(row.original.PatientPassportNum)}</td>
                        <td>${replaceNullOrUndefined(row.original.FlightNumber)}</td>
                        <td>Patient</td>
                        <td>${row.original.Remarks ?? ''}</td>
                    </tr>
                    ${companionsRows}
                `;
        }).join('')}
        </tbody>
    </table>
    <div class="footer">
        IH-CUS-009-00
    </div>
</body>
</html>
`;

        const modifiedHtml = addNumberColumn(htmlContent);

        const userChoice = window.confirm("Click ok to proceed print, or click cancel to download as doc format.");

        if (userChoice) {
            // User chose to print
            const printWindow = window.open('', '_blank');
            printWindow.document.write(modifiedHtml);
            printWindow.document.close();
            printWindow.print();
        } else {
            // User chose to download as Word document
            const converted = htmlDocx.asBlob(modifiedHtml);
            const url = URL.createObjectURL(converted);
            const a = document.createElement('a');
            a.href = url;
            a.download = `DriverForm.docx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
        /*
        const printWindow = window.open('', '_blank');
        printWindow.document.write(modifiedHtml);
        printWindow.document.close();

        // 2. Prepare the content for Word document (docx)
        const doc = new Docxtemplater();
        doc.load(modifiedHtml, { xmlFile: true });

        // Convert the HTML content to a DOCX document
        try {
            doc.render();
            const buffer = doc.getZip().generate({ type: 'blob' });
            saveAs(buffer, 'document.docx');
        } catch (error) {
            console.error('Error generating document:', error);
        }
      

   
        const printWindow = window.open('', '_blank');
        printWindow.document.write(modifiedHtml);
        printWindow.document.close();
        printWindow.print();
         */
        
    };



    const addNumberColumn = (html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const style = `
  <style>
    body {
        padding: 5px; /* size of the padding around the text */
        margin: 0 auto;
        max-width: 800px;
    }
    table {
        border-collapse: collapse;
        width: 100%;
        font-size: 12px; 
    }
    th, td {
        border: 1px solid black;
        padding: 3px; /* size of the padding around the text */
        text-align: left;
    }
    th.index {
        width: 50px;
    }
    th, td {
        text-align: center;
    }
    .footer {
        position: fixed;
        bottom: 0;
        right: 0;
        margin: 0; /* Adjusted margin */
    }
    /* Ensure footer is visible on every printed page */
    @media print {
        .footer {
            position: fixed;
            bottom: 0;
            right: 0;
            margin: 0;
        }
    }
  </style>
`;


        const styleElement = parser.parseFromString(style, 'text/html').querySelector('style');
        doc.head.appendChild(styleElement);

        const table = doc.querySelector('table');
        const thead = table.querySelector('thead tr');
        const tbody = table.querySelector('tbody');

        // Add a new header cell
        const th = doc.createElement('th');
        th.textContent = '#';
        th.classList.add('index');
        thead.insertBefore(th, thead.firstChild);

        // Add new numbered cells to each row
        Array.from(tbody.rows).forEach((row, index) => {
            const td = doc.createElement('td');
            td.textContent = index + 1;
            row.insertBefore(td, row.firstChild);
        });

        return doc.documentElement.outerHTML;
    };


    const columns = useMemo(() => {
        if (!filteredData || filteredData.length === 0 || !filteredData[0]) return [];
        
        const baseColumns = [
            {
                Header: '#',
                accessor: 'index',
                Cell: ({ row }) => row.index + 1
            }
        ];

        // Get all keys except the ones we want to exclude
        const excludeKeys = ['Indexs', 'ApproveByDate', 'UniqueLink', 'isNotify'];
        
        // For mobile data, exclude Actions and Remarks
        if (activeTab === 'mobile') {
            excludeKeys.push('Actions', 'Remarks');
        }

        // For all data tab, exclude Actions and Remarks from mobile data
        if (activeTab === 'all') {
            excludeKeys.push('Actions', 'Remarks');
        }

        const dataColumns = Object.keys(filteredData[0] || {})
            .filter(key => !excludeKeys.includes(key))
            .map(key => ({
                Header: key === 'TargetDate' ? 'Target Date' : key === 'dataSource' ? 'Data Source' : splitCamelCase(key),
                accessor: key,
                Cell: ({ value, row }) => {
                    if (key === 'CreateByDate') {
                        return sqFormatDate(value);
                    }
                    if (key === 'TargetDate') {
                        return <strong>{sqFormatDate(value).substring(0, 10)}</strong>;
                    }
                    if (key === 'FlightItinerary' && value) {
                        if (activeTab === 'portal' || (activeTab === 'all' && row.original.dataSource === 'portal')) {
                            return <a href={`${process.env.REACT_APP_AIRPORT_PICKUP_SERVER}/documents/flightItinerary/${row.original.UniqueLink}.${row.original.FlightItinerary}`} target="_blank" rel="noopener noreferrer">View</a>;
                        } else {
                            // For mobile data, display as string
                            return value;
                        }
                    }
                    if (key === 'Status') {
                        const statusColor = value === 'Approve' ? '#50C878' : value.includes('Reject') ? 'red' : 'black';
                        const showTooltip = value === 'Approve' || value.includes('Reject');
                        const tooltipText = showTooltip 
                            ? `Approve/Reject date: ${row.original.ApproveByDate ? sqFormatDate(row.original.ApproveByDate) : 'null'}`
                            : '';
                        
                        return (
                            <span 
                                style={{ 
                                    color: statusColor, 
                                    fontWeight: 'w800',
                                    cursor: showTooltip && isTouchDevice ? 'pointer' : undefined,
                                }}
                                title={!isTouchDevice && showTooltip ? tooltipText : undefined}
                                onClick={() => {
                                    if (showTooltip && isTouchDevice) {
                                        window.alert(tooltipText);
                                    }
                                }}
                            >
                                {value}
                            </span>
                        );
                    }
                    if (key === 'dataSource') {
                        return <span style={{ 
                            backgroundColor: value === 'portal' ? '#e3f2fd' : '#f3e5f5', 
                            padding: '2px 6px', 
                            borderRadius: '3px',
                            fontSize: '11px',
                            fontWeight: 'bold'
                        }}>{value}</span>;
                    }
                    return value === null ? '-' : value;
                }
            }));

        // Insert Remarks column before Status column only for portal data
        const statusIndex = dataColumns.findIndex(col => col.accessor === 'Status');
        if (statusIndex !== -1 && (activeTab === 'portal' || (activeTab === 'all' && filteredData.some(item => item.dataSource === 'portal')))) {
            const remarksColumn = {
                Header: 'Remarks',
                accessor: 'Remarks',
                Cell: ({ value, row }) => {
                    // Only show remarks for portal data
                    if (activeTab === 'all' && row.original.dataSource === 'mobile') {
                        return <span style={{ color: '#999', fontSize: '12px' }}>N/A</span>;
                    }
                    return value === null || value === undefined ? '-' : value;
                }
            };
            dataColumns.splice(statusIndex, 0, remarksColumn);
        }

        // Add Actions column only for portal data and all data (but only for portal entries)
        if (activeTab === 'portal' || activeTab === 'all') {
            dataColumns.push({
                Header: 'Actions',
                accessor: 'actions',
                Cell: ({ row }) => {
                    // For all data tab, only show actions for portal entries
                    if (activeTab === 'all' && row.original.dataSource === 'mobile') {
                        return <span style={{ color: '#999', fontSize: '12px' }}>N/A</span>;
                    }
                    
                    return (
                        <div>
                            {row.original.Status === 'Pending' && (
                                <div>
                                    <button className="approve-btn" onClick={() => handleApprove(row)}>Approve</button>
                                    <button className="reject-btn" onClick={() => handleReject(row)}>Reject</button>
                                </div>
                            )}
                            {(row.original.Status === 'Approve' || row.original.Status.includes('Reject')) && (
                                <button className="notify-btn" onClick={() => handleNotify(row)}><MdEmail style={{ marginRight: '2px' }} />
                                    {row.original.isNotify ? 'Notified' : 'Notify'}
                                </button>
                            )}
                        </div>
                    );
                }
            });
        }

        return [...baseColumns, ...dataColumns];
    }, [filteredData, activeTab, isTouchDevice]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state,
        setGlobalFilter,
    } = useTable(
        { columns, data: filteredData },
        useFilters,
        useGlobalFilter,
        useSortBy
    );

    const pageCount = Math.ceil(rows.length / itemsPerPage);

    const currentRows = useMemo(() => {
        const startIndex = currentPage * itemsPerPage;
        return rows.slice(startIndex, startIndex + itemsPerPage);
    }, [currentPage, rows]);

    const csvHeaders = columns.map(col => ({ label: col.Header, key: col.accessor }));

    // Handler for Excel upload
    const handleExcelUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
            // Send to backend
            const response = await fetch(`${process.env.REACT_APP_AIRPORT_PICKUP_SERVER}/api/uploadMobileData`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ data: jsonData }),
            });
            if (response.ok) {
                window.alert('Excel data uploaded successfully!');
                fetchData(); // Refresh data
            } else {
                const errorData = await response.json();
                window.alert(`Failed to upload: ${errorData.error || 'Unknown error'}`);
            }
        } catch (error) {
            window.alert('Error uploading Excel: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    if (localStorage.getItem("currentuser") === null) {
        navigate('/adminLogin')
        return (
            <div>
                <h1>Please login to continue</h1>
                <button className="logout-button" onClick={() => { navigate('/adminLogin'); }}>Login</button>
            </div>
        );
    }

    return (
        <div>
            
            <Navbar currentPage='AdminPortal' />

              

            <div className="app-container">       
            <h1>Admin Portal</h1>
            
            {/* Tab Interface */}
            <div className="tab-container">
                <button 
                    className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                >
                    All Data
                </button>
                <button 
                    className={`tab-button ${activeTab === 'portal' ? 'active' : ''}`}
                    onClick={() => setActiveTab('portal')}
                >
                    Portal Data
                </button>
                <button 
                    className={`tab-button ${activeTab === 'mobile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('mobile')}
                >
                    Mobile Data
                </button>
            </div>

            <div className="admin-date-picker-container">
                <DatePicker
                    selected={startDate}
                    onChange={date => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    placeholderText="Start Date"
                    className='react-datepicker'
                />
                <DatePicker
                    selected={endDate}
                    onChange={date => setEndDate(date)}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    placeholderText="End Date"
                    className='react-datepicker'
                />
                <select
                    value={selectedTimeSlot}
                    onChange={e => setSelectedTimeSlot(e.target.value)}
                    className="time-slot-dropdown"
                >
                    <option value="">Select Time Slot</option>
                    {timeSlots.map((slot, index) => (
                        <option key={index} value={slot}>{slot}</option>
                    ))}
                </select>
            </div>

            <div className="admin-action-buttons">
                <CSVLink
                    data={rows.map(row => row.original)}
                    headers={csvHeaders}
                    filename={`${activeTab === 'portal' ? 'portal' : activeTab === 'mobile' ? 'mobile' : 'all'}_airport_pickup_data_${startDate ? startDate.toLocaleDateString() : ''}_to_${endDate ? endDate.toLocaleDateString() : ''}.csv`}
                    className="csv-link"
                >
                    <TbDownload style={{ marginRight: '5px' }} /> Export to CSV
                </CSVLink>
                {activeTab === 'all' && (
                    <button className="driver-button" onClick={handlePrintSlip}>
                        <TbDownload style={{ marginRight: '5px' }} /> Driver Slip
                    </button>
                )}
            </div>
      
            <div className="admin-update-buttons">
               {/*  <button className="update-button" onClick={updateSpecialist}><HiDocumentText style={{ marginRight: '2px' }} />Update Specialist</button>*/}
                {activeTab === 'all' && (
                    <button className="update-button" onClick={updateHoliday}><HiDocumentText style={{ marginRight: '2px' }} />Update Holiday</button>
                )}
                {activeTab === 'all' && (
                    <div className="upload-mobile-wrapper">
                        <label htmlFor="excel-upload" className="update-button upload-mobile-label" style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
                            <HiDocumentText style={{ marginRight: '2px' }} />Upload Mobile Data
                            <input
                                id="excel-upload"
                                type="file"
                                accept=".xlsx,.xls"
                                style={{ display: 'none' }}
                                onChange={handleExcelUpload}
                                disabled={uploading}
                            />
                        </label>
                        <span 
                            style={{ 
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                cursor: 'pointer',
                                color: '#007bff',
                                fontSize: '18px',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #007bff',
                                zIndex: 1
                            }}
                            onClick={() => navigate('/question')}
                            title="Upload Mobile Data Instructions"
                        >
                            <IoHelpCircle />
                        </span>
                    </div>
                )}
            </div>

            <div className="search-box">
                <input
                    type="search"
                    value={state.globalFilter || ""}
                    onChange={e => setGlobalFilter(e.target.value)}
                    placeholder="Search..."
                />
            </div>
            <div>
                <table {...getTableProps()} className="admin-table">
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        className={column.isSorted ? (column.isSortedDesc ? 'sorted-desc' : 'sorted-asc') : 'sortable-header'}
                                    >
                                        {column.render('Header')}
                                        <span>
                                            {column.isSorted ? (
                                                column.isSortedDesc ? <MdOutlineArrowDropDown /> : <MdOutlineArrowDropUp />
                                            ) : (
                                                <MdOutlineArrowDropDown className="sort-icon" />
                                            )}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {currentRows.map(row => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            
                <div className="pagination">
                    <button
                        className="pagination-button"
                        onClick={() => setCurrentPage(0)}
                        disabled={currentPage === 0}
                        title="Go to first page"
                    >
                        {'<<'}
                    </button>
                    <button
                        className="pagination-button"
                        onClick={() =>
                            setCurrentPage((old) =>
                                Math.max(old - 1, 0)
                            )
                        }
                        disabled={currentPage === 0}
                        title="Go to previous page"
                    >
                        {'<'}
                    </button>
                    <div className="pagination-info">
                        Page {currentPage + 1} of {pageCount} • Showing {currentRows.length} of {rows.length} records
                    </div>
                    <button
                        className="pagination-button"
                        onClick={() =>
                            setCurrentPage((old) =>
                                Math.min(old + 1, pageCount - 1)
                            )
                        }
                        disabled={currentPage === pageCount - 1}
                        title="Go to next page"
                    >
                        {'>'}
                    </button>
                    <button
                        className="pagination-button"
                        onClick={() => setCurrentPage(pageCount - 1)}
                        disabled={currentPage === pageCount - 1}
                        title="Go to last page"
                    >
                        {'>>'}
                    </button>
                </div>
            </div>
            </div>
        </div>
    );
}

export default AdminPortal;